import { Content, List, Root, Trigger } from '@radix-ui/react-tabs';
import { clsx } from 'clsx';
import Icon from '../Icon';
import type { TabsProps } from './Tabs.types';

const Tabs = ({
  tabs,
  defaultActiveTab,
  activeTab,
  onTabSelect,
  extraClassNames,
  testId,
  id,
  stretchTabs = false,
}: TabsProps) => {
  const handleTabSelect = (tabId: string) => {
    if (activeTab === tabId) return;
    onTabSelect && onTabSelect(tabId);
  };

  return (
    <Root
      className={`tw:flex tw:max-w-full tw:min-w-75 tw:grow tw:flex-col tw:focus-visible:outline-none ${
        extraClassNames || ''
      }`}
      defaultValue={defaultActiveTab}
      data-testid={testId}
      id={id}
      value={activeTab}
      onValueChange={handleTabSelect}>
      <List className="tw:z-1001 tw:flex tw:flex-row tw:flex-wrap">
        {tabs.map(
          (
            { id, icon, label, tabExtraClassNames, testId: tabsTestId, disabled, tabLabelExtraClassNames = '' },
            index,
          ) => (
            <Trigger
              className={clsx(
                `tw:flex tw:min-w-25 tw:flex-1 tw:items-center tw:justify-center tw:border-r-2 tw:border-solid tw:border-sq-overlay tw:bg-sq-white tw:px-4 tw:last:border-r-0 tw:focus-visible:outline-none tw:dark:border-gray-700 tw:dark:bg-sq-dark-background`,
                {
                  'tw:max-w-max tw:min-w-fit': !stretchTabs,
                  'tw:border-b-[0.1875rem] tw:border-b-sq-theme-darker tw:dark:border-b-sq-theme-darker':
                    activeTab === id,
                  'tw:border-b-[0.0625rem] tw:hover:bg-sq-light-gray tw:hover:dark:bg-gray-700': activeTab !== id,
                },
                tabExtraClassNames,
              )}
              data-testid={tabsTestId}
              disabled={disabled}
              key={`${label}-${id}-${index}`}
              value={id}>
              <span className="tw:flex tw:items-center tw:overflow-hidden">
                {icon && (
                  <Icon
                    icon={icon}
                    testId={`${id}_tab-icon`}
                    extraClassNames="tw:text-sq-15 tw:mr-sq-7 tw:flex-shrink-0"
                  />
                )}
                <span
                  className={`tw:overflow-hidden tw:text-sq-13 tw:font-medium tw:text-ellipsis tw:whitespace-nowrap ${
                    activeTab === id
                      ? 'tw:text-gray-500 tw:dark:text-sq-dark-text'
                      : 'tw:text-sq-theme-dark tw:dark:text-sq-theme-darker'
                  } ${tabLabelExtraClassNames}`}>
                  {label}
                </span>
              </span>
            </Trigger>
          ),
        )}
      </List>
      {tabs.map((tab, index) => (
        <Content
          className={`tw:z-500 tw:-mt-sq-1 tw:flex tw:grow tw:flex-col tw:overflow-y-auto tw:border-t-[0.0625rem] tw:border-t-sq-overlay tw:bg-sq-white tw:focus-visible:outline-none tw:dark:border-t-gray-700 tw:dark:bg-sq-dark-background ${
            tab.tabContentExtraClassNames || ''
          }`}
          key={`${tab.label}_${index}_content`}
          value={tab.id}>
          {tab.content}
        </Content>
      ))}
    </Root>
  );
};

export default Tabs;
